/**
 * 结算结果页 - 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Result, Button, Spin, Typography, Space } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleFilled,
} from '@ant-design/icons';
import StorefrontLayout from '../../components/storefront/StorefrontLayout';
import { orderApi } from '../../api/index';

const { Text } = Typography;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
};

// 支付结果状态枚举
const STATUS = {
  LOADING: 'loading',   // 查询中
  SUCCESS: 'success',   // 支付成功
  FAILED: 'failed',     // 支付失败
  PENDING: 'pending',   // 支付待确认
};

export default function CheckoutResultPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 从 URL 读取参数
  const reference = searchParams.get('reference') || '';
  const orderId = searchParams.get('id') || '';
  const abandonedOrderNo = searchParams.get('abandonedOrderNo') || '';
  const statusParam = searchParams.get('status') || '';

  // 当前展示状态
  const [resultStatus, setResultStatus] = useState(STATUS.LOADING);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  /**
   * 查询订单真实支付状态
   */
  useEffect(() => {
    let cancelled = false;

    const fetchOrder = async () => {
      if (!reference && !orderId && !abandonedOrderNo) {
        setResultStatus(STATUS.PENDING);
        return;
      }

      try {
        // 查询顺序：弃单号 > 订单号 > reference
        let data;
        if (abandonedOrderNo) {
          // 通过弃单号查询正式订单
          data = await orderApi.getByAbandonedOrderId(abandonedOrderNo);
        } else if (orderId) {
          // orderId 实际是订单号（如 Y1511222048）
          data = await orderApi.getOrder(orderId);
        } else {
          data = await orderApi.getByOrderNo(reference);
        }

        if (cancelled) return;
        setOrder(data);

        // 如果 URL 中 status=success（来自 NihaoPay 回调），且订单不是 cancelled/refunded，视为成功
        if (statusParam === 'success') {
          if (data?.financialStatus === 'paid') {
            setResultStatus(STATUS.SUCCESS);
          } else if (data?.financialStatus === 'refunded' || data?.status === 'cancelled') {
            setResultStatus(STATUS.FAILED);
          } else {
            // URL 显示成功但订单财务状态还不是 paid，可能是 IPN 还没处理完，显示成功
            setResultStatus(STATUS.SUCCESS);
          }
          return;
        }

        // 没有 status 参数时，根据订单 financialStatus 判断
        if (data?.financialStatus === 'paid') {
          setResultStatus(STATUS.SUCCESS);
        } else if (data?.financialStatus === 'refunded' || data?.status === 'cancelled') {
          setResultStatus(STATUS.FAILED);
        } else {
          setResultStatus(STATUS.PENDING);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('查询订单失败:', err);
        setError(err.message || '查询失败');

        // 如果 URL 显示 success 但查询失败，可能是订单还未创建，轮询支付状态
        if (statusParam === 'success' && abandonedOrderNo) {
          try {
            const paymentData = await orderApi.queryPaymentStatus(abandonedOrderNo);
            if (!cancelled) {
              if (paymentData?.order) {
                setOrder(paymentData.order);
                setResultStatus(STATUS.SUCCESS);
              } else {
                setResultStatus(STATUS.PENDING);
              }
            }
          } catch (pollErr) {
            console.error('轮询支付状态失败:', pollErr);
            // 仍然显示成功，因为 NihaoPay 回调说成功了
            setResultStatus(STATUS.SUCCESS);
          }
        } else if (statusParam === 'success') {
          setResultStatus(STATUS.SUCCESS);
        } else {
          setResultStatus(STATUS.PENDING);
        }
      }
    };

    fetchOrder();

    // 如果显示待确认，定时轮询支付状态
    let pollInterval = null;
    if (resultStatus === STATUS.PENDING && abandonedOrderNo) {
      pollInterval = setInterval(async () => {
        try {
          const paymentData = await orderApi.queryPaymentStatus(abandonedOrderNo);
          if (paymentData?.order) {
            setOrder(paymentData.order);
            if (paymentData.order.financialStatus === 'paid') {
              setResultStatus(STATUS.SUCCESS);
            }
          }
        } catch (pollErr) {
          console.error('轮询支付状态失败:', pollErr);
        }
      }, 3000); // 每 3 秒轮询一次
    }

    return () => {
      cancelled = true;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [reference, orderId, abandonedOrderNo, statusParam, resultStatus]);

  // ===== 渲染加载状态 =====
  if (resultStatus === STATUS.LOADING) {
    return (
      <StorefrontLayout>
        <div
          style={{
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '80px 24px',
          }}
        >
          <Spin size="large" />
          <Text style={{ marginTop: 24, fontSize: 16, color: COLORS.lightInk }}>
            正在确认支付状态，请稍候...
          </Text>
        </div>
      </StorefrontLayout>
    );
  }

  // ===== 渲染成功状态 =====
  if (resultStatus === STATUS.SUCCESS) {
    return (
      <StorefrontLayout>
        <div style={{ maxWidth: 600, margin: '60px auto', padding: '0 24px' }}>
          <Result
            icon={
              <CheckCircleFilled
                style={{ color: COLORS.bambooGreen, fontSize: 72 }}
              />
            }
            status="success"
            title={<span style={{ color: COLORS.ink }}>订单支付成功！</span>}
            subTitle={
              <Space direction="vertical" align="center">
                <Text style={{ color: COLORS.lightInk }}>感谢您的购买，我们将尽快为您安排发货。</Text>
                {reference && (
                  <Text style={{ color: COLORS.lightInk }}>
                    订单编号：<Text strong style={{ color: COLORS.ink }}>{reference}</Text>
                  </Text>
                )}
              </Space>
            }
            extra={[
              <Button
                type="primary"
                key="orders"
                size="large"
                onClick={() => navigate('/orders')}
                style={{
                  borderRadius: 8,
                  background: COLORS.teaBrown,
                  borderColor: COLORS.teaBrown,
                }}
              >
                查看我的订单
              </Button>,
              <Button
                key="home"
                size="large"
                onClick={() => navigate('/')}
                style={{
                  borderRadius: 8,
                  borderColor: COLORS.teaBrown,
                  color: COLORS.teaBrown,
                }}
              >
                返回首页
              </Button>,
            ]}
          />
        </div>
      </StorefrontLayout>
    );
  }

  // ===== 渲染失败状态 =====
  if (resultStatus === STATUS.FAILED) {
    return (
      <StorefrontLayout>
        <div style={{ maxWidth: 600, margin: '60px auto', padding: '0 24px' }}>
          <Result
            icon={
              <CloseCircleFilled
                style={{ color: COLORS.cinnabar, fontSize: 72 }}
              />
            }
            status="error"
            title={<span style={{ color: COLORS.ink }}>订单已取消</span>}
            subTitle={
              <Space direction="vertical" align="center">
                <Text style={{ color: COLORS.lightInk }}>您的订单已取消或退款，如有疑问请联系我们。</Text>
                {reference && (
                  <Text style={{ color: COLORS.lightInk }}>
                    订单编号：<Text strong style={{ color: COLORS.ink }}>{reference}</Text>
                  </Text>
                )}
              </Space>
            }
            extra={[
              <Button
                type="primary"
                key="orders"
                size="large"
                onClick={() => navigate('/orders')}
                style={{
                  borderRadius: 8,
                  background: COLORS.teaBrown,
                  borderColor: COLORS.teaBrown,
                }}
              >
                查看我的订单
              </Button>,
              <Button
                key="home"
                size="large"
                onClick={() => navigate('/')}
                style={{
                  borderRadius: 8,
                  borderColor: COLORS.teaBrown,
                  color: COLORS.teaBrown,
                }}
              >
                返回首页
              </Button>,
            ]}
          />
        </div>
      </StorefrontLayout>
    );
  }

  // ===== 渲染待确认状态 =====
  return (
    <StorefrontLayout>
      <div style={{ maxWidth: 600, margin: '60px auto', padding: '0 24px' }}>
        <Result
          icon={
            <ClockCircleFilled
              style={{ color: '#D4B483', fontSize: 72 }}
            />
          }
          status="warning"
          title={<span style={{ color: COLORS.ink }}>支付待确认</span>}
          subTitle={
            <Space direction="vertical" align="center">
              <Text style={{ color: COLORS.lightInk }}>您的支付正在处理中，请稍后查看订单状态。</Text>
              {order && (
                <Text style={{ color: COLORS.lightInk }}>
                  订单金额：<Text strong style={{ color: COLORS.ink }}>${order?.total?.toFixed(2) || '0.00'}</Text>
                </Text>
              )}
              {reference && (
                <Text style={{ color: COLORS.lightInk }}>
                  订单编号：<Text strong style={{ color: COLORS.ink }}>{reference}</Text>
                </Text>
              )}
              <Button
                type="primary"
                danger
                onClick={() => navigate(`/checkout?reference=${reference}`)}
                style={{
                  borderRadius: 8,
                  background: COLORS.cinnabar,
                  borderColor: COLORS.cinnabar,
                }}
              >
                继续支付
              </Button>
            </Space>
          }
          extra={[
            <Button
              key="orders"
              size="large"
              onClick={() => navigate('/orders')}
              style={{
                borderRadius: 8,
                borderColor: COLORS.teaBrown,
                color: COLORS.teaBrown,
              }}
            >
              查看我的订单
            </Button>,
            <Button
              key="home"
              size="large"
              onClick={() => navigate('/')}
              style={{
                borderRadius: 8,
                borderColor: COLORS.teaBrown,
                color: COLORS.teaBrown,
              }}
            >
              返回首页
            </Button>,
          ]}
        />
      </div>
    </StorefrontLayout>
  );
}
